import React, { useState, useContext, Component } from 'react';
import { BarChart3Icon, UsersIcon, TableIcon, FileTextIcon, GlobeIcon, DollarSignIcon, PackageIcon, UmbrellaIcon, MessagesSquareIcon, ClockIcon, BuildingIcon, TruckIcon, CreditCardIcon, ListIcon, TentIcon, QrCodeIcon, SettingsIcon, ShieldIcon, SearchIcon, BellIcon, UserIcon, WarehouseIcon, ArrowRightIcon, ChevronDownIcon, ChevronRightIcon, HomeIcon, NetworkIcon, TrendingUpIcon } from 'lucide-react';
const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Administrative Configurator',
  matrix: 'Matrix',
  items: 'Items',
  span: 'Span Config',
  overhang: 'Overhang Config',
  sprinkler: 'Sprinkler Config',
  regions: 'Region and Countries',
  territory: 'Territory Management',
  incoterm: 'Incoterm',
  pricelist: 'Price List',
  warehouse: 'Warehouse',
  payment: 'Payment Terms',
  transportation: 'Type Of Transportation',
  fairs: 'Agricultural Fairs',
  qrcode: 'QR Code Generator',
  version: 'Version Management',
  cron: 'Cron Tasks',
  feedback: 'Feedback',
  icontrol: 'iControl Remote',
  users: 'Users'
};
function moduleLabel(module: string) {
  return MODULE_LABELS[module] || module.charAt(0).toUpperCase() + module.slice(1);
}
import { FairsManagement } from '../components/admin/FairsManagement';
import { QRCodeGenerator } from '../components/admin/QRCodeGenerator';
import { UsersManagement } from '../components/admin/UsersManagement';
import { TerritoryManagement } from '../components/admin/TerritoryManagement';
import { FairContext } from '../contexts/FairContext';
export function AdminPage() {
  const [activeModule, setActiveModule] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentFair, setCurrentFair] = useState(null);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const {
    setActiveFair
  } = useContext(FairContext);
  const handleModuleChange = module => {
    setActiveModule(module);
  };
  const handleGenerateQR = fair => {
    setCurrentFair(fair);
    setIsGeneratingQR(true);
    setActiveModule('qrcode');
  };
  const handleQRGenerated = fairId => {
    // When QR is generated successfully, update the fair to have QR
    const updatedFair = {
      ...currentFair,
      hasQRCode: true
    };
    // Set as active fair to trigger the announcement on home page
    setActiveFair(updatedFair);
    // Log to confirm the context is updated
    console.log('QR generated for fair:', updatedFair);
    setIsGeneratingQR(false);
    setCurrentFair(null);
    setActiveModule('fairs');
  };
  const renderModuleContent = () => {
    switch (activeModule) {
      case 'fairs':
        return <FairsManagement onGenerateQR={handleGenerateQR} data-id="element-6992" />;
      case 'qrcode':
        return isGeneratingQR && currentFair ? <QRCodeGenerator fair={currentFair} onGenerated={handleQRGenerated} onCancel={() => {
          setIsGeneratingQR(false);
          setCurrentFair(null);
          setActiveModule('fairs');
        }} data-id="element-6993" /> : <div className="flex items-center justify-center h-64 bg-white/5 backdrop-blur-sm rounded-xl p-6" data-id="element-6994">
            <div className="text-center" data-id="element-6995">
              <h2 className="text-xl font-medium text-white mb-2" data-id="element-6996">
                QR Code Generator
              </h2>
              <p className="text-blue-300" data-id="element-6997">
                Please select a fair first to generate a QR code
              </p>
            </div>
          </div>;
      case 'users':
        return <UsersManagement data-id="element-6998" />;
      case 'territory':
        return <TerritoryManagement data-id="element-6999" />;
      case 'dashboard':
        return <AdminDashboard setActiveModule={setActiveModule} data-id="element-7000" />;
      default:
        return <div className="flex items-center justify-center h-64 bg-white/5 backdrop-blur-sm rounded-xl p-6" data-id="element-7001">
            <div className="text-center" data-id="element-7002">
              <h2 className="text-xl font-medium text-white mb-2" data-id="element-7003">
                {activeModule.charAt(0).toUpperCase() + activeModule.slice(1)}{' '}
                Module
              </h2>
              <p className="text-blue-300" data-id="element-7004">This module is under development</p>
            </div>
          </div>;
    }
  };
  return <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-7005">
      {/* Admin Header — Quotes V2 style top bar */}
      <header className="bg-gradient-to-r from-blue-950/80 via-indigo-950/60 to-purple-950/50 backdrop-blur-sm border-b border-white/10 px-6 relative z-10 flex-shrink-0 h-[72px] flex items-center" data-id="element-7006">
        {/* Breadcrumb navigation — left aligned */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 flex-shrink-0" data-id="element-7007">
          <HomeIcon size={15} className="text-blue-300/60 hover:text-blue-200 cursor-pointer transition-colors flex-shrink-0" onClick={() => setActiveModule('dashboard')} data-id="element-7008" />
          <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-7009" />
          {activeModule === 'dashboard' ? <span className="text-sm font-semibold text-white" data-id="element-7010">Admin</span> : <>
              <button onClick={() => setActiveModule('dashboard')} className="text-xs text-blue-300/60 hover:text-blue-200 transition-colors" data-id="element-7011">
                Admin
              </button>
              <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-7012" />
              <span className="text-sm font-semibold text-white" data-id="element-7013">
                {moduleLabel(activeModule)}
              </span>
            </>}
        </nav>
      </header>
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-7014">
        {activeModule === 'dashboard' && <>
            <div className="mb-6" data-id="element-7015">
              <h1 className="text-2xl font-bold text-white" data-id="element-7016">
                Administrative configurator
              </h1>
              <p className="text-blue-300 mt-1" data-id="element-7017">
                Manage system settings, configurations, and administrative tasks
              </p>
            </div>
            {/* Product and Component Configuration */}
            <div className="mb-8" data-id="element-7018">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7019">
                <PackageIcon size={20} className="mr-2 text-blue-400" data-id="element-7020" />
                Product and Component Configuration
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7021">
                <ConfigCard icon={<TableIcon size={24} data-id="element-7023" />} title="Matrix" description="Assignment of properties to sets and kits for validation and selection in designs." onClick={() => handleModuleChange('matrix')} data-id="element-7022" />
                <ConfigCard icon={<PackageIcon size={24} data-id="element-7025" />} title="Items" description="Management of available items/products." onClick={() => handleModuleChange('items')} data-id="element-7024" />
                <ConfigCard icon={<div className="w-6 h-6 flex items-center justify-center bg-blue-400/20 rounded text-lg font-bold" data-id="element-7027">
                      S
                    </div>} title="Span Config" description="Configuration of spans used in irrigation systems." onClick={() => handleModuleChange('span')} data-id="element-7026" />
                <ConfigCard icon={<UmbrellaIcon size={24} data-id="element-7029" />} title="Overhang Config" description="Configuration of overhangs for project designs." onClick={() => handleModuleChange('overhang')} data-id="element-7028" />
                <ConfigCard icon={<div className="w-6 h-6 flex items-center justify-center bg-blue-400/20 rounded text-lg font-bold" data-id="element-7031">
                      S
                    </div>} title="Sprinkler Config" description="Configuration of sprinklers and their technical parameters." onClick={() => handleModuleChange('sprinkler')} data-id="element-7030" />
              </div>
            </div>
            {/* Commercial and Logistics Setup */}
            <div className="mb-8" data-id="element-7032">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7033">
                <GlobeIcon size={20} className="mr-2 text-green-400" data-id="element-7034" />
                Commercial and Logistics Setup
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7035">
                <ConfigCard icon={<GlobeIcon size={24} data-id="element-7037" />} title="Region and Countries" description="Definition of regions and countries for geographic assignment." onClick={() => handleModuleChange('regions')} data-id="element-7036" />
                <ConfigCard icon={<NetworkIcon size={24} data-id="element-7039" />} title="Territory Management" description="Manage regional access and default quote responsibilities." onClick={() => handleModuleChange('territory')} data-id="element-7038" />
                <ConfigCard icon={<ListIcon size={24} data-id="element-7041" />} title="Incoterm" description="Setup of international trade terms (FOB, CIF, etc.)." onClick={() => handleModuleChange('incoterm')} data-id="element-7040" />
                <ConfigCard icon={<DollarSignIcon size={24} data-id="element-7043" />} title="Price List" description="Administration of price lists by region, customer, or product." onClick={() => handleModuleChange('pricelist')} data-id="element-7042" />
                <ConfigCard icon={<WarehouseIcon size={24} data-id="element-7045" />} title="Warehouse" description="Management of warehouses and stock locations." onClick={() => handleModuleChange('warehouse')} data-id="element-7044" />
                <ConfigCard icon={<CreditCardIcon size={24} data-id="element-7047" />} title="Payment Terms" description="Setup of payment conditions applicable to customers and quotations." onClick={() => handleModuleChange('payment')} data-id="element-7046" />
                <ConfigCard icon={<TruckIcon size={24} data-id="element-7049" />} title="Type Of Transportation" description="Configuration of logistics and transport options." onClick={() => handleModuleChange('transportation')} data-id="element-7048" />
              </div>
            </div>
            {/* Fairs and Promotions */}
            <div className="mb-8" data-id="element-7050">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7051">
                <TentIcon size={20} className="mr-2 text-amber-400" data-id="element-7052" />
                Fairs and Promotions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7053">
                <ConfigCard icon={<TentIcon size={24} data-id="element-7055" />} title="Agricultural Fairs" description="Register and manage agricultural fairs participation." onClick={() => handleModuleChange('fairs')} highlight={true} data-id="element-7054" />
                <ConfigCard icon={<QrCodeIcon size={24} data-id="element-7057" />} title="QR Code Generator" description="Create promotional QR codes with automatic discounts." onClick={() => handleModuleChange('qrcode')} highlight={true} data-id="element-7056" />
              </div>
            </div>
            {/* Version Control and Automations */}
            <div className="mb-8" data-id="element-7058">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7059">
                <SettingsIcon size={20} className="mr-2 text-purple-400" data-id="element-7060" />
                Version Control and Automations
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7061">
                <ConfigCard icon={<ListIcon size={24} data-id="element-7063" />} title="Version Management" description="Control and tracking of system versions and configurations." onClick={() => handleModuleChange('version')} data-id="element-7062" />
                <ConfigCard icon={<ClockIcon size={24} data-id="element-7065" />} title="Cron Tasks" description="Scheduled tasks and system automations." onClick={() => handleModuleChange('cron')} data-id="element-7064" />
              </div>
            </div>
            {/* Other Elements */}
            <div className="mb-8" data-id="element-7066">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7067">
                <FileTextIcon size={20} className="mr-2 text-blue-400" data-id="element-7068" />
                Other Elements
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7069">
                <ConfigCard icon={<MessagesSquareIcon size={24} data-id="element-7071" />} title="Feedback" description="Review and management of user comments or system feedback." onClick={() => handleModuleChange('feedback')} data-id="element-7070" />
                <ConfigCard icon={<ShieldIcon size={24} data-id="element-7073" />} title="iControl Remote" description="Configuration of integration with remote irrigation control systems." onClick={() => handleModuleChange('icontrol')} data-id="element-7072" />
                <ConfigCard icon={<UsersIcon size={24} data-id="element-7075" />} title="Users" description="User account management and access control." onClick={() => handleModuleChange('users')} data-id="element-7074" />
              </div>
            </div>
          </>}
        {/* Show module content when a module is selected */}
        {activeModule !== 'dashboard' && <div data-id="element-7076">{renderModuleContent()}</div>}
      </div>
    </div>;
}
function ConfigCard({
  icon,
  title,
  description,
  onClick,
  highlight = false
}) {
  return <button onClick={onClick} className={`bg-white/5 backdrop-blur-sm border ${highlight ? 'border-amber-500/50 ring-1 ring-amber-500/30' : 'border-white/10'} rounded-xl p-5 flex flex-col items-start gap-3 hover:bg-white/10 transition-colors text-left h-full group`} data-id="element-7077">
      <div className={`p-2.5 rounded-lg ${highlight ? 'bg-amber-500/10 text-amber-400' : 'bg-white/5 text-blue-400 group-hover:bg-blue-500/20 group-hover:text-blue-300 transition-colors'}`} data-id="element-7078">
        {icon}
      </div>
      <div data-id="element-7079">
        <span className="font-semibold text-white block mb-1" data-id="element-7080">{title}</span>
        <p className="text-xs text-blue-300/70 leading-relaxed" data-id="element-7081">
          {description}
        </p>
      </div>
    </button>;
}
function AdminDashboard({
  setActiveModule
}) {
  const stats = [{
    title: 'Total Users',
    value: 248,
    change: '+12%',
    trend: 'up',
    icon: <UsersIcon size={20} className="text-blue-400" data-id="element-7082" />
  }, {
    title: 'Active Dealers',
    value: 86,
    change: '+5%',
    trend: 'up',
    icon: <BuildingIcon size={20} className="text-emerald-400" data-id="element-7083" />
  }, {
    title: 'Products',
    value: 1254,
    change: '+3%',
    trend: 'up',
    icon: <PackageIcon size={20} className="text-amber-400" data-id="element-7084" />
  }, {
    title: 'Upcoming Fairs',
    value: 5,
    change: 'New',
    trend: 'none',
    icon: <TentIcon size={20} className="text-purple-400" data-id="element-7085" />
  }];
  const quickActions = [{
    title: 'Register Fair',
    description: 'Add a new agricultural fair',
    icon: <TentIcon size={20} className="text-amber-400" data-id="element-7086" />,
    module: 'fairs'
  }, {
    title: 'Generate QR Code',
    description: 'Create promotional QR code',
    icon: <QrCodeIcon size={20} className="text-purple-400" data-id="element-7087" />,
    module: 'qrcode'
  }, {
    title: 'Manage Price Lists',
    description: 'Update product pricing',
    icon: <DollarSignIcon size={20} className="text-emerald-400" data-id="element-7088" />,
    module: 'pricelist'
  }, {
    title: 'Configure Products',
    description: 'Manage product catalog',
    icon: <PackageIcon size={20} className="text-blue-400" data-id="element-7089" />,
    module: 'items'
  }];
  return <div className="space-y-6" data-id="element-7090">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7091">
        {stats.map((stat, index) => <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5" data-id="element-7092">
            <div className="flex justify-between items-start mb-2" data-id="element-7093">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-7094">{stat.icon}</div>
              <div className="mt-1 text-xs font-medium" data-id="element-7095">
                {stat.trend === 'up' && <span className="text-emerald-400 flex items-center" data-id="element-7096">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-7097" />
                    {stat.change}
                  </span>}
                {stat.trend === 'down' && <span className="text-red-400 flex items-center" data-id="element-7098">
                    <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-7099" />
                    {stat.change}
                  </span>}
                {stat.trend === 'none' && <span className="text-amber-400 flex items-center" data-id="element-7100">
                    {stat.change}
                  </span>}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-id="element-7101">
              {stat.value.toLocaleString()}
            </h3>
            <p className="text-sm text-blue-300/70" data-id="element-7102">{stat.title}</p>
          </div>)}
      </div>
      {/* Quick Actions */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5" data-id="element-7103">
        <h3 className="text-lg font-semibold text-white mb-4" data-id="element-7104">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7105">
          {quickActions.map((action, index) => <button key={index} onClick={() => setActiveModule(action.module)} className="bg-white/5 border border-white/5 rounded-xl p-4 text-left transition-colors hover:bg-white/10 flex flex-col h-full group" data-id="element-7106">
              <div className="p-2.5 bg-white/5 rounded-lg inline-flex mb-3 group-hover:bg-white/10 transition-colors" data-id="element-7107">
                {action.icon}
              </div>
              <h3 className="text-sm font-semibold text-white mb-1" data-id="element-7108">
                {action.title}
              </h3>
              <p className="text-xs text-blue-300/70 mt-1 leading-relaxed" data-id="element-7109">
                {action.description}
              </p>
              <div className="mt-4 text-xs font-medium text-blue-400 flex items-center mt-auto opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7110">
                Access
                <ArrowRightIcon size={12} className="ml-1" data-id="element-7111" />
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}